import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import "./EditorLeftMenuUploadControls.css";
import { useEditorStore } from "../../../../state/editorStore";
import RoundLoader from "../../../../components/ui/RoundLoader";
import FadeInMedia from "../../../../components/ui/FadeInMedia";
import GalleryLoadMore from "../../../../components/manage/GalleryLoadMore";
import ImagePickerModal from "../../../../components/popups/imagePicker/ImagePickerModal";
import { useImagePickerModal } from "../../../../hooks/app/useImagePickerModal";
import {
  libraryItemCreationId,
  libraryItemExportId,
  libraryItemDisplayName,
} from "../../../../utils/openLibraryInEditor";
import {
  ensureCreationLabel,
  fetchUploadImageBlob,
  fetchCreationImageBlob,
  fetchExportImageBlob,
  getUploads,
  mapUploadItems,
  registerLibraryUpload,
} from "../../../../services/api/editor/background_removal";
import { isFallbackClassifyLabel } from "../../../../utils/classifyImageLabel";
import { getUploadSizeError } from "../../../../utils/uploadLimits";

/** First page + each "Show more" page — keeps initial bandwidth low. */
const EDITOR_UPLOADS_PAGE_SIZE = 15;

function uploadRootId(item) {
  const fromId = Number(String(item.id || "").replace(/^upload:/, ""));
  return (
    item.root_creation_id ??
    item.rootCreationId ??
    item.creation_id ??
    (Number.isFinite(fromId) && fromId > 0 ? fromId : null)
  );
}

function errorDetail(err) {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || String(x)).join(", ");
  return err?.message || "Upload failed";
}

function hasUsableImageUrl(url) {
  return (
    typeof url === "string" &&
    url.length > 0 &&
    !url.startsWith("pending-fetch:")
  );
}

function mapApiUpload(item) {
  const rootId = uploadRootId(item);
  const apiLabel =
    typeof item.label === "string" && item.label.trim() ? item.label.trim() : "";
  // API object/empty → "Object" (never Upload.webp / Image).
  const label =
    apiLabel && !isFallbackClassifyLabel(apiLabel) ? apiLabel : "Object";
  return {
    id: item.id || `upload:${rootId}`,
    label,
    imageUrl: item.output_url || null,
    output_url: item.output_url || null,
    rootCreationId: rootId,
    root_creation_id: rootId,
    created_at: item.created_at,
  };
}

/** Per-tile lazy image resolve + RoundLoader fade-in. */
function UploadLibraryTile({ asset, onPlace }) {
  const { getToken } = useAuth();
  const upsertUploadAsset = useEditorStore((s) => s.upsertUploadAsset);
  const [src, setSrc] = useState(() =>
    hasUsableImageUrl(asset.imageUrl) ? asset.imageUrl : null
  );
  const [preferBlob, setPreferBlob] = useState(
    () => !hasUsableImageUrl(asset.imageUrl) && !asset.output_url
  );
  const [exhausted, setExhausted] = useState(false);

  useEffect(() => {
    let cancelled = false;

    if (hasUsableImageUrl(asset.imageUrl) && !preferBlob) {
      setSrc(asset.imageUrl);
      return undefined;
    }
    if (asset.output_url && !preferBlob) {
      setSrc(asset.output_url);
      return undefined;
    }

    const rootId = asset.rootCreationId ?? uploadRootId(asset);
    if (!rootId) {
      setExhausted(true);
      return undefined;
    }

    (async () => {
      try {
        const token = await getToken();
        if (!token || cancelled) return;
        const blob = await fetchUploadImageBlob(token, rootId);
        if (cancelled) return;
        if (blob && blob.type && blob.type.includes("json")) {
          throw new Error("Upload image API returned JSON error");
        }
        const objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
        upsertUploadAsset({
          id: asset.id,
          layerId: asset.layerId ?? null,
          label: asset.label,
          imageUrl: objectUrl,
          rootCreationId: rootId,
          createdAt: asset.createdAt || Date.now(),
        });
      } catch {
        if (!cancelled) setExhausted(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [asset, getToken, upsertUploadAsset, preferBlob]);

  return (
    <button
      type="button"
      className="upload-panel__tile"
      title={asset.label || "Place on canvas"}
      onClick={() => onPlace(asset.id, src)}
    >
      <FadeInMedia
        src={src}
        alt={asset.label || ""}
        className="fade-in-media--auto-height"
        objectFit="contain"
        loaderSize="sm"
        waiting={!exhausted}
        draggable={false}
        onError={() => {
          if ((asset.output_url || hasUsableImageUrl(asset.imageUrl)) && !preferBlob) {
            setPreferBlob(true);
            setSrc(null);
            return;
          }
          setExhausted(true);
        }}
      />
    </button>
  );
}

const EditorLeftMenuUploadControls = ({
  getCanvasSize,
  onPlacedLayer,
}) => {
  const { getToken } = useAuth();
  const { openImagePicker, imagePickerProps } = useImagePickerModal();
  const uploadAssets = useEditorStore((s) => s.uploadAssets);
  const [loading, setLoading] = useState(() => (uploadAssets || []).length === 0);
  const [uploading, setUploading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [remoteLoadedCount, setRemoteLoadedCount] = useState(0);
  const remoteSkipRef = useRef(0);

  const addUploadAsset = useEditorStore((s) => s.addUploadAsset);
  const upsertUploadAsset = useEditorStore((s) => s.upsertUploadAsset);
  const mergeRemoteUploadAssets = useEditorStore((s) => s.mergeRemoteUploadAssets);
  const placeUploadAssetOnCanvas = useEditorStore((s) => s.placeUploadAssetOnCanvas);
  const selectLayer = useEditorStore((s) => s.selectLayer);

  const fetchUploadsPage = useCallback(
    async (skip, { isInitial = false } = {}) => {
      const token = await getToken();
      if (!token) return;
      const data = await getUploads(token, {
        skip,
        limit: EDITOR_UPLOADS_PAGE_SIZE,
      });
      const raw = data?.uploads || [];
      const items = mapUploadItems(data).map(mapApiUpload);
      mergeRemoteUploadAssets(items);
      const nextSkip = skip + raw.length;
      remoteSkipRef.current = nextSkip;
      setRemoteLoadedCount(nextSkip);
      setTotalCount(Number(data?.total_count) || nextSkip);
      setHasMore(Boolean(data?.has_more));
      if (isInitial && items.length === 0) {
        /* empty library */
      }
    },
    [getToken, mergeRemoteUploadAssets]
  );

  useEffect(() => {
    let cancelled = false;

    const loadFirstPage = async () => {
      const hasExisting = (useEditorStore.getState().uploadAssets || []).length > 0;
      if (!hasExisting) setLoading(true);
      setError(null);
      try {
        // Always refresh first page so Show more skip stays correct
        remoteSkipRef.current = 0;
        await fetchUploadsPage(0, { isInitial: true });
      } catch (err) {
        if (!cancelled) {
          setError(errorDetail(err) || "Failed to load uploads");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void loadFirstPage();
    return () => {
      cancelled = true;
    };
  }, [fetchUploadsPage]);

  const handleShowMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    setError(null);
    try {
      await fetchUploadsPage(remoteSkipRef.current);
    } catch (err) {
      setError(errorDetail(err) || "Failed to load more uploads");
    } finally {
      setLoadingMore(false);
    }
  };

  const addFileToLibrary = async (file) => {
    if (!file || !file.type.startsWith("image/")) return;
    const sizeError = getUploadSizeError(file);
    if (sizeError) {
      setError(sizeError);
      return;
    }
    const localUrl = URL.createObjectURL(file);
    const pendingId = `pending_${Date.now().toString(36)}_${Math.random()
      .toString(36)
      .slice(2, 6)}`;

    addUploadAsset({ id: pendingId, imageUrl: localUrl, label: "Object" });

    setUploading(true);
    setError(null);
    try {
      const token = await getToken();
      if (!token) {
        setError("Sign in required to save uploads to your library");
        return;
      }
      const result = await registerLibraryUpload(token, file);
      const rootId = result?.root_creation_id ?? result?.creation_id ?? result?.id;
      if (!rootId) {
        throw new Error(result?.detail || "Upload failed");
      }
      let classifiedLabel =
        (typeof result.label === "string" && result.label.trim()) || "Object";
      // Backend classify can miss on cold start — ensure-label classifies + persists.
      if (isFallbackClassifyLabel(classifiedLabel)) {
        try {
          const ensured = await ensureCreationLabel(token, rootId);
          if (
            typeof ensured?.label === "string" &&
            ensured.label.trim() &&
            !isFallbackClassifyLabel(ensured.label)
          ) {
            classifiedLabel = ensured.label.trim();
          } else {
            classifiedLabel = "Object";
          }
        } catch {
          classifiedLabel = "Object";
        }
      }
      upsertUploadAsset(
        {
          id: `upload:${rootId}`,
          layerId: null,
          label: classifiedLabel,
          imageUrl: result.output_url || localUrl,
          rootCreationId: rootId,
          createdAt: Date.now(),
        },
        { replaceId: pendingId }
      );
      setTotalCount((n) => n + 1);
    } catch (err) {
      const status = err?.response?.status;
      const detail = errorDetail(err);
      if (status === 404) {
        setError(
          "Could not save to cloud library (upload API not found). Image stays available in this session — restart the bgremove API to enable sync."
        );
      } else {
        setError(detail);
      }
    } finally {
      setUploading(false);
    }
  };

  const handleFiles = (fileList) => {
    Array.from(fileList || []).forEach((file) => {
      void addFileToLibrary(file);
    });
  };

  const handleAssetClick = async (assetId, resolvedSrc) => {
    const canvasSize = getCanvasSize?.();
    if (!canvasSize?.width) return;
    const asset = (useEditorStore.getState().uploadAssets || []).find(
      (a) => a.id === assetId
    );
    if (!asset) return;

    const rootId = asset.rootCreationId ?? uploadRootId(asset);
    let placeSrc = resolvedSrc || asset.imageUrl;

    // Always place a blob:/data: URL so Remove BG never browser-fetches MinIO/S3 (CORS).
    const isRemote =
      typeof placeSrc === "string" &&
      /^https?:\/\//i.test(placeSrc) &&
      !placeSrc.startsWith("blob:") &&
      !placeSrc.startsWith("data:");

    if (isRemote && rootId) {
      try {
        const token = await getToken();
        if (!token) {
          setError("Sign in required to place uploads");
          return;
        }
        const blob = await fetchUploadImageBlob(token, rootId);
        if (!blob || (blob.type && String(blob.type).includes("json"))) {
          throw new Error("Upload image API returned an error");
        }
        placeSrc = URL.createObjectURL(blob);
      } catch (err) {
        setError(err?.message || "Could not load upload for the canvas");
        return;
      }
    }

    upsertUploadAsset({
      ...asset,
      imageUrl: placeSrc || asset.imageUrl,
      rootCreationId: rootId ?? asset.rootCreationId ?? null,
    });

    const id = await placeUploadAssetOnCanvas(assetId, canvasSize);
    if (id) {
      selectLayer(id);
      onPlacedLayer?.(id);

      // Legacy rows may lack display_label — ensure once (classify + persist).
      const needsLabel =
        !asset.label ||
        isFallbackClassifyLabel(asset.label) ||
        asset.label.length > 28;
      if (needsLabel && rootId) {
        void (async () => {
          try {
            const token = await getToken();
            if (!token) return;
            const data = await ensureCreationLabel(token, rootId);
            const label =
              typeof data?.label === "string" && data.label.trim()
                ? data.label.trim()
                : null;
            if (!label || isFallbackClassifyLabel(label)) return;
            const latest = (useEditorStore.getState().uploadAssets || []).find(
              (a) => a.id === assetId
            );
            if (latest) {
              upsertUploadAsset({ ...latest, label });
            }
            useEditorStore.getState().updateImageLayer(id, { label });
          } catch {
            /* ensure-label is best-effort */
          }
        })();
      }
    }
  };

  const placeLibraryItemOnCanvas = async (item) => {
    const canvasSize = getCanvasSize?.();
    if (!canvasSize?.width) return;
    try {
      const token = await getToken();
      if (!token) {
        setError("Sign in required");
        return;
      }
      let blob;
      let assetId;
      let rootId;
      if (item.kind === "upload") {
        rootId = libraryItemCreationId(item);
        assetId = `upload:${rootId}`;
        blob = await fetchUploadImageBlob(token, rootId);
      } else if (item.kind === "export") {
        const exportId = libraryItemExportId(item);
        assetId = `export:${exportId}`;
        blob = await fetchExportImageBlob(token, exportId);
      } else {
        rootId = libraryItemCreationId(item);
        assetId = `creation:${rootId}`;
        blob = await fetchCreationImageBlob(token, rootId);
      }
      const url = URL.createObjectURL(blob);
      upsertUploadAsset({
        id: assetId,
        layerId: null,
        label: libraryItemDisplayName(item),
        imageUrl: url,
        rootCreationId: rootId || null,
        createdAt: Date.now(),
      });
      await handleAssetClick(assetId, url);
    } catch (err) {
      setError(err?.message || "Could not place that image");
    }
  };

  const openAddImagePicker = () => {
    openImagePicker({
      onPickFile: (file) => handleFiles([file]),
      onPickLibrary: (item) => {
        void placeLibraryItemOnCanvas(item);
      },
    });
  };

  return (
    <div className="editor-left-menu-upload-controls-wrapper">
      <ImagePickerModal {...imagePickerProps} />
      <h1>Upload</h1>
      <p className="upload-panel__hint">
        Your dashboard uploads appear here. Click a thumbnail to place it on the
        canvas.
      </p>
      <button
        type="button"
        className="upload-panel__add-btn"
        onClick={openAddImagePicker}
        disabled={uploading}
      >
        {uploading ? "Uploading…" : "+ Add image"}
      </button>
      {error && <p className="upload-panel__error">{error}</p>}

      {loading ? (
        <div className="upload-panel__loading">
          <RoundLoader size="md" label="Loading uploads…" />
        </div>
      ) : (uploadAssets || []).length === 0 ? (
        <p className="upload-panel__empty">No uploads yet</p>
      ) : (
        <>
          {/*
            Two independent columns (even/odd) = masonry packing:
            newest left, 2nd newest right, 3rd under left, … — no row-height gaps.
          */}
          <div className="upload-panel__grid">
            <div className="upload-panel__col">
              {(uploadAssets || [])
                .filter((_, i) => i % 2 === 0)
                .map((asset) => (
                  <UploadLibraryTile
                    key={asset.id}
                    asset={asset}
                    onPlace={handleAssetClick}
                  />
                ))}
            </div>
            <div className="upload-panel__col">
              {(uploadAssets || [])
                .filter((_, i) => i % 2 === 1)
                .map((asset) => (
                  <UploadLibraryTile
                    key={asset.id}
                    asset={asset}
                    onPlace={handleAssetClick}
                  />
                ))}
            </div>
          </div>
          <div className="upload-panel__more">
            <GalleryLoadMore
              loadedCount={remoteLoadedCount || (uploadAssets || []).length}
              totalCount={totalCount}
              hasMore={hasMore}
              isLoadingMore={loadingMore}
              onLoadMore={handleShowMore}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default EditorLeftMenuUploadControls;
